import { createApi } from '@reduxjs/toolkit/query/react';

import { TagTypesEnum } from '@enum/tag-types.enum';
import { ResponseDataInterface } from '@interface/logs/response-data.interface';
import {
  AddMemberInputInterface,
  InviteInfoInterface,
  MemberChangeRolInputInterface,
  MemberInterface,
} from '@interface/member/member.interface';
import { portalQueryAuth } from '@util/api/portal-query-auth';
import { isExist } from '@util/data/is-exist';

export const membersApi = createApi({
  reducerPath: 'member-api',
  tagTypes: [TagTypesEnum.Members],
  baseQuery: portalQueryAuth('/members'),
  endpoints: (build) => ({
    loadMembers: build.query<ResponseDataInterface<MemberInterface[]>, string>({
      query: (account: string) => `/?account=${account}`,
      providesTags: [TagTypesEnum.Members],
    }),
    addMember: build.mutation<ResponseDataInterface<MemberInterface[]>, AddMemberInputInterface>({
      query: (body: AddMemberInputInterface) => ({
        url: '/invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result: any, error: any) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    acceptInvite: build.mutation<ResponseDataInterface<MemberInterface>, string>({
      query: (code: string) => ({
        url: `/invite/${code}/accept`,
        method: 'POST',
      }),
      invalidatesTags: (result: any, error: any) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    getInviteInfo: build.query<ResponseDataInterface<InviteInfoInterface>, string>({
      query: (code: string) => ({
        url: `/invite/${code}/info`,
        method: 'POST',
      }),
    }),
    changeRole: build.mutation<ResponseDataInterface<MemberInterface[]>, MemberChangeRolInputInterface>({
      query: ({ id, account, role }: { id: string; account: string; role: string }) => ({
        url: `/role/${id}`,
        method: 'POST',
        body: { account, role },
      }),
      invalidatesTags: (result: any, error: any) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    suspendMember: build.mutation<ResponseDataInterface<MemberInterface[]>, { id: string; account: string }>({
      query: ({ id, account }: { id: string; account: string }) => ({
        url: `/suspend/${id}`,
        method: 'POST',
        body: { account },
      }),
      invalidatesTags: (result: any, error: any) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    deleteMember: build.mutation<ResponseDataInterface<MemberInterface[]>, { id: string; account: string }>({
      query: ({ id, account }: { id: string; account: string }) => ({
        url: `/${id}`,
        method: 'DELETE',
        body: { account },
      }),
      invalidatesTags: (result: any, error: any) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
  }),
});
